import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Download, Copy, Share2, Calendar, Users, 
  DollarSign, Star, Clock, Video, ExternalLink,
  Sparkles, Trophy, Target
} from 'lucide-react';

const RECRUITMENT_CARDS = [
  {
    id: 1,
    headline: "SALON OWNERS: The Opportunity of a LIFETIME",
    subheadline: "Join Our National Sales Team",
    description: "Earn LIFETIME recurring payments on every stylist you recruit. Limited to 50 founding partners.",
    cta: "Book Your Zoom Call",
    tag: "LIMITED SPOTS",
    tagColor: "bg-red-500",
    image: "https://static.prod-images.emergentagent.com/jobs/f2cfdc73-219d-495e-86ce-39c038134787/images/8aa33575f4964d4215690ad857e1b86589963d09930e3257d972c1a5e93920dd.png",
    benefits: ["$10 signup bonus", "$10 first month", "$8/mo recurring FOREVER"],
    zoomLink: "https://zoom.us/j/myhairchair-soldier-recruitment"
  },
  {
    id: 2,
    headline: "BUILD GENERATIONAL WEALTH",
    subheadline: "Passive Income for Life",
    description: "Our founding soldiers earn recurring commissions on their entire network. This opportunity closes at 50 members.",
    cta: "Schedule Strategy Call",
    tag: "EXCLUSIVE",
    tagColor: "bg-purple-500",
    image: "https://static.prod-images.emergentagent.com/jobs/f2cfdc73-219d-495e-86ce-39c038134787/images/e1fbf9297cff0108dd3ec83fe9de171be790a57144f0ae43bb816f814df398e7.png",
    benefits: ["3-year commission tail", "Death benefits for family", "No cap on earnings"],
    zoomLink: "https://zoom.us/j/myhairchair-wealth-building"
  },
  {
    id: 3,
    headline: "50 STYLISTS. LIFETIME INCOME.",
    subheadline: "Become a MyHairChair Soldier",
    description: "We're building an elite national sales force. Be one of 50 founding partners and secure your financial future.",
    cta: "Claim Your Spot",
    tag: "ONLY 50 SPOTS",
    tagColor: "bg-amber-500",
    image: "https://static.prod-images.emergentagent.com/jobs/f2cfdc73-219d-495e-86ce-39c038134787/images/a069fad501a07a3637a03fb9e3c95d294015d1cc4e390f8a90809c3075e61ad8.png",
    benefits: ["Lifetime recurring revenue", "National sales team", "Training & support included"],
    zoomLink: "https://zoom.us/j/myhairchair-founding-50"
  }
];

export default function SoldierRecruitmentCards() {
  const [copiedId, setCopiedId] = useState(null);

  const handleCopyLink = async (card) => {
    const shareText = `🚀 ${card.headline}\n\n${card.description}\n\n✅ ${card.benefits.join('\n✅ ')}\n\n📅 Book your Zoom call: ${card.zoomLink}\n\n#MyHairChair #SalonOwners #PassiveIncome`;
    
    try {
      await navigator.clipboard.writeText(shareText);
      setCopiedId(card.id);
      toast.success('Caption copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleDownloadImage = async (card) => {
    try {
      const response = await fetch(card.image);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `myhairchair-recruitment-${card.id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Image downloaded!');
    } catch (err) {
      toast.error('Failed to download image');
    }
  };

  const handleShare = async (card) => {
    const shareData = {
      title: card.headline,
      text: card.description,
      url: card.zoomLink
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink(card);
        }
      }
    } else {
      handleCopyLink(card);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-400" />
            Soldier Recruitment Cards
          </h2>
          <p className="text-stone-400 text-sm mt-1">
            Share these cards on social media to recruit new soldiers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            <Target className="w-3 h-3 mr-1" />
            50 Spots Total
          </Badge>
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
            <Video className="w-3 h-3 mr-1" />
            Zoom Meetings
          </Badge>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {RECRUITMENT_CARDS.map((card) => (
          <Card 
            key={card.id} 
            className="bg-stone-900 border-stone-700 overflow-hidden hover:border-amber-500/50 transition-all"
          >
            {/* Card Image */}
            <div className="relative">
              <img 
                src={card.image} 
                alt={card.headline}
                className="w-full h-48 object-cover"
              />
              <Badge className={`absolute top-3 left-3 ${card.tagColor} text-white font-bold`}>
                {card.tag}
              </Badge>
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-transparent to-transparent" />
            </div>

            <CardContent className="p-5 space-y-4">
              {/* Headlines */}
              <div>
                <h3 className="text-xl font-bold text-amber-400 leading-tight">
                  {card.headline}
                </h3>
                <p className="text-white font-medium mt-1">{card.subheadline}</p>
              </div>

              {/* Description */}
              <p className="text-stone-400 text-sm">{card.description}</p>

              {/* Benefits */}
              <div className="space-y-2">
                {card.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <DollarSign className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-stone-300">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={() => handleDownloadImage(card)}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
                <Button
                  onClick={() => handleCopyLink(card)}
                  size="sm"
                  variant="outline"
                  className="border-stone-600 text-stone-300"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  {copiedId === card.id ? 'Copied!' : 'Copy Caption'}
                </Button>
                <Button
                  onClick={() => handleShare(card)}
                  size="sm"
                  variant="outline"
                  className="border-stone-600 text-stone-300"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Share
                </Button>
              </div>

              {/* Zoom Link */}
              <div className="pt-2 border-t border-stone-700">
                <a 
                  href={card.zoomLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300"
                >
                  <Video className="w-4 h-4" />
                  <span className="truncate">{card.zoomLink}</span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Preview Section */}
      <Card className="bg-gradient-to-br from-stone-900 to-stone-950 border-amber-500/30">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" />
            Social Media Caption Template
          </h3>
          <div className="bg-stone-950 rounded-lg p-4 font-mono text-sm text-stone-300 whitespace-pre-wrap">
{`🚀 SALON OWNERS: The Opportunity of a LIFETIME

Are you ready to build GENERATIONAL WEALTH?

Join the MyHairChair National Sales Team and earn:
✅ $10 signup bonus per stylist
✅ $10 first month commission
✅ $8/month RECURRING FOREVER

⚠️ LIMITED TO 50 FOUNDING PARTNERS

This isn't just a job—it's a LEGACY.

📅 Book your exclusive Zoom call today!
🔗 [Your Zoom Link]

#MyHairChair #SalonOwners #PassiveIncome #BeautyIndustry #Entrepreneur #WealthBuilding`}
          </div>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(`🚀 SALON OWNERS: The Opportunity of a LIFETIME

Are you ready to build GENERATIONAL WEALTH?

Join the MyHairChair National Sales Team and earn:
✅ $10 signup bonus per stylist
✅ $10 first month commission
✅ $8/month RECURRING FOREVER

⚠️ LIMITED TO 50 FOUNDING PARTNERS

This isn't just a job—it's a LEGACY.

📅 Book your exclusive Zoom call today!

#MyHairChair #SalonOwners #PassiveIncome #BeautyIndustry #Entrepreneur #WealthBuilding`);
              toast.success('Template copied!');
            }}
            className="mt-4 bg-amber-500 hover:bg-amber-600 text-black"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy Template
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
